import { useEffect } from 'react';
import { proStudioUrqlGql } from '@pypestream/api-services';

import { toDeploymentProgressRows } from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';

/**
 * Keeps `ctx.solutionDeployments` in sync with the open solution's deployment rows.
 *
 * One subscription for the whole app, over the same `graphql-ws` socket everything else
 * uses, feeding both the publish toast and the Versions environment cards — a publish is one
 * event and every viewer must see the same thing, so it would be wrong for two consumers to
 * hold two independently-timed copies of it.
 *
 * Mounted once, in the router root: publishing is announced wherever the user happens to be
 * (workflows, files, channels…), not only on the Versions page.
 */
export function useSolutionDeployments(): void {
  const solutionId = useProStudioCtxSelector(
    (ctx) => ctx.selectedBot?.uniqueId ?? null
  );

  useEffect(() => {
    if (!solutionId) return undefined;

    const { subscribe } = proStudioUrqlGql.solutionDeploymentsSubscription({
      solutionId,
    });

    const { unsubscribe } = subscribe((subData) => {
      if (subData.error) return;
      const rows = subData.data?.tenancy_solution_deployments;
      if (!rows) return;
      sendProStudioEvent({
        type: 'setSolutionDeployments',
        deployments: toDeploymentProgressRows(rows),
      });
    });

    return () => {
      unsubscribe();
      // Clear on the way out: the next solution's rows arrive a round trip later, and until
      // they do, the cards must not show the previous solution's publish state.
      sendProStudioEvent({ type: 'setSolutionDeployments', deployments: [] });
    };
  }, [solutionId]);
}
